import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/UI/Button';
import {
  MatchingProgressBox,
  MatchingProgressContent,
  MatchingProgressDescription,
  MatchingProgressSpinner,
  MatchingProgressTitle,
  MatchingProgressWarning,
} from './styles';

type Props = {
  status?: string | null;
  elapsedMs: number;
  consecutiveFailures: number;
  autoStopped: boolean;
  onCheckStatus: () => void;
};

const MatchingProgressPanel = forwardRef<HTMLHeadingElement, Props>(
  ({ status, elapsedMs, consecutiveFailures, autoStopped, onCheckStatus }, headingRef) => {
    const { t } = useTranslation();
    const key = 'pbl.pagesHardcoded.ocManageCases.manageCaseDetail.matchingWait';
    const delayed = elapsedMs >= 60_000;
    const titleKey = autoStopped
      ? 'automaticChecksPausedTitle'
      : delayed
        ? 'takingLongerTitle'
        : status === 'running'
          ? 'runningTitle'
          : 'queuedTitle';
    const descriptionKey = autoStopped
      ? 'automaticChecksPausedDescription'
      : delayed
        ? 'takingLongerDescription'
        : status === 'running'
          ? 'runningDescription'
          : 'queuedDescription';

    return (
      <MatchingProgressBox role="status" aria-live="polite" aria-atomic="true">
        {!autoStopped && <MatchingProgressSpinner aria-hidden="true" />}
        <MatchingProgressContent>
          <MatchingProgressTitle ref={headingRef} tabIndex={-1}>{t(`${key}.${titleKey}`)}</MatchingProgressTitle>
          <MatchingProgressDescription>{t(`${key}.${descriptionKey}`)}</MatchingProgressDescription>
          {consecutiveFailures >= 2 && (
            <MatchingProgressWarning>{t(`${key}.connectionWarning`)}</MatchingProgressWarning>
          )}
        </MatchingProgressContent>
        {(delayed || consecutiveFailures >= 2 || autoStopped) && (
          <Button type="button" size="md" variant="primary-invert" rounded={false} onClick={onCheckStatus}>
            {consecutiveFailures >= 2 ? t(`${key}.tryAgain`) : t(`${key}.checkStatus`)}
          </Button>
        )}
      </MatchingProgressBox>
    );
  }
);

MatchingProgressPanel.displayName = 'MatchingProgressPanel';

export default MatchingProgressPanel;
